import React, { useEffect, useRef, useCallback } from 'react';
import type { EnhancedDialogProps } from '../types/ui';
import './EnhancedDialog.css';

/**
 * Skyline Dialog — replaces Blueprint Dialog.
 * Supports: isOpen, onClose, title, canEscapeKeyClose, canOutsideClickClose, children.
 */
const EnhancedDialog: React.FC<EnhancedDialogProps> = ({
  children,
  isOpen,
  onClose,
  title,
  canEscapeKeyClose = true,
  canOutsideClickClose = true,
  className,
  style,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus dialog on open
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      dialogRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Escape key handling
  useEffect(() => {
    if (!isOpen || !canEscapeKeyClose) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, canEscapeKeyClose, onClose]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (canOutsideClickClose && e.target === e.currentTarget) {
      onClose?.();
    }
  }, [canOutsideClickClose, onClose]);

  if (!isOpen) return null;

  return (
    <div className="sky-dialog-overlay" onClick={handleOverlayClick}>
      <div
        className={className ? `sky-dialog ${className}` : 'sky-dialog'}
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        style={style}
      >
        {title && (
          <div className="sky-dialog-header">
            <h3 className="sky-dialog-title">{title}</h3>
            <button className="sky-dialog-close" onClick={() => onClose?.()} aria-label="Close">
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default EnhancedDialog;
